import { firebaseService } from './firebase.js';
import { encryptionService, encryptionMigrationService, SENSITIVE_FIELDS } from '../utils/encryption.js';
import { regionService } from './region.js';
import { hexToRgb } from '../utils/color-utils.js';

// System font fallback stack
const FONT_FALLBACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Format font name with quotes (if multi-word) and fallback stack
function formatFontValue(name) {
  if (!name) return null;
  const quoted = name.includes(' ') ? `'${name}'` : name;
  return `${quoted}, ${FONT_FALLBACK}`;
}

// Apply font CSS variables from agency data
export function applyFonts(agency) {
  const heading = agency?.headingFont || agency?.heading;
  const body = agency?.bodyFont || agency?.body;
  const root = document.documentElement.style;
  if (heading) {
    const val = formatFontValue(heading);
    root.setProperty('--font-heading', val);
  }
  if (body) {
    const val = formatFontValue(body);
    root.setProperty('--font-body', val);
    root.setProperty('--font-family', val);
  }
}

// Apply primary color and its RGB variant for use with rgba()
export function applyPrimaryColor(hex) {
  if (!hex) return;
  document.documentElement.style.setProperty('--primary-color', hex);
  const rgb = hexToRgb(hex);
  if (rgb) {
    document.documentElement.style.setProperty('--brand-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
  }
}

// Apply secondary color and its RGB variant
export function applySecondaryColor(hex) {
  if (!hex) return;
  document.documentElement.style.setProperty('--secondary-color', hex);
  const rgb = hexToRgb(hex);
  if (rgb) {
    document.documentElement.style.setProperty('--brand-secondary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
  }
}

class UserService {
  constructor() {
    this.currentUser = null;
    this.currentAgency = null;
    this.currentClient = null;
    this.userRole = null;
    this.listeners = [];
  }

  async initializeUser(firebaseUser, additionalData = null) {
    if (!firebaseUser) {
      this.clearUserData();
      return null;
    }

    try {
      // Get or create user document
      let user = await firebaseService.getUser(firebaseUser.uid);

      if (!user) {
        // First time login - create user document
        user = await this.createNewUser(firebaseUser, additionalData);
      }

      // Update lastLoginAt timestamp
      try {
        await firebaseService.updateUser(firebaseUser.uid, {
          lastLoginAt: new Date().toISOString()
        });
        user.lastLoginAt = new Date().toISOString();
      } catch (loginUpdateError) {
        // silent
      }

      this.currentUser = user;
      this.userRole = user.role;

      // Carregar moeda do usuário no regionService (moeda é imutável após cadastro)
      if (user.currency) {
        regionService.setUserCurrency(user.currency);
      }

      // Load additional data based on role
      if (user.role === 'agency' || user.role === 'agency_member') {
        // For agency/member users, load agency data
        if (user.agencyId) {
          await this.loadAgencyData(user.agencyId);
        } else if (user.role === 'agency') {
          // Only owners can create agency
          await this.createAgencyForUser(additionalData);
        }
      } else if (user.role === 'client') {
        await this.loadClientData(user.agencyId, user.clientId);
      }

      this.notifyListeners();
      return user;
    } catch (error) {
      throw error;
    }
  }

  async createNewUser(firebaseUser, additionalData = null) {
    try {
      // Detectar moeda baseada na região do usuário (será fixada permanentemente)
      let currency = additionalData?.currency;
      let country = additionalData?.country;

      if (!currency) {
        const regionData = await regionService.getInitialCurrency();
        currency = regionData.currency;
        country = regionData.country;
      }

      // Setar moeda no regionService
      regionService.setUserCurrency(currency);

      // NOTA: role é permitido no create, mas agencyId, isPremium, etc. são gerenciados por Cloud Functions
      // NOTA: subscriptionStatus e emailVerified são campos protegidos (Firestore rules).
      // Serão setados pelo Cloud Function sendVerificationCode (pending_verification)
      // e verifyEmailCode (trial). Sem esses campos, subscription-guard retorna status 'none'.
      const userData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: additionalData?.agencyName || firebaseUser.displayName || '',
        photoURL: firebaseUser.photoURL || '',
        phone: additionalData?.phone || '',
        currency: currency, // Moeda fixada - não muda após cadastro
        country: country || 'BR', // País detectado
        role: 'agency', // Novos usuários sempre começam como 'agency'
        status: 'active',
        createdAt: new Date().toISOString()
      };

      const newUser = await firebaseService.createUser(userData);
      return newUser;
    } catch (error) {
      throw error;
    }
  }

  async loadAgencyData(agencyId) {
    try {
      let agency = await firebaseService.getAgency(agencyId);

      if (!agency && this.userRole === 'agency') {
        // Create agency if user is agency owner but agency doesn't exist
        agency = await this.createAgencyForUser();
      }

      this.currentAgency = agency;

      // Apply saved fonts from agency data
      applyFonts(agency);

      // Apply saved colors from agency data
      if (agency?.primaryColor) {
        applyPrimaryColor(agency.primaryColor);
      }
      if (agency?.secondaryColor) {
        applySecondaryColor(agency.secondaryColor);
      }

      return agency;
    } catch (error) {
      throw error;
    }
  }

  async createAgencyForUser(additionalData = null) {
    if (!this.currentUser || this.userRole !== 'agency') {
      throw new Error('Cannot create agency for non-agency user');
    }

    try {
      // Use agencyName from signup form, or fallback to displayName
      const agencyName = additionalData?.agencyName ||
                         this.currentUser.displayName ||
                         'Minha Agência';

      const agencyData = {
        name: agencyName,
        email: this.currentUser.email,
        phone: additionalData?.phone || this.currentUser.phone || '',
        currency: this.currentUser.currency || 'BRL', // Herda moeda do user
        country: this.currentUser.country || 'BR', // Herda país do user
        website: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'Brasil'
        }
      };

      const agency = await firebaseService.createAgency(agencyData, this.currentUser.uid);
      this.currentAgency = agency;

      // Update user with agencyId (role já foi setado no create)
      await firebaseService.updateUser(this.currentUser.uid, {
        agencyId: agency.id
      });

      // Reinitialize client service now that agency is available
      const { clientService } = await import('./client.js');
      await clientService.reinitialize();

      return agency;
    } catch (error) {
      throw error;
    }
  }

  async loadClientData(agencyId, clientId) {
    try {
      const client = await firebaseService.getClient(agencyId, clientId);
      this.currentClient = client;
      
      // Also load agency data for context
      const agency = await firebaseService.getAgency(agencyId);
      this.currentAgency = agency;
      
      return client;
    } catch (error) {
      throw error;
    }
  }

  isAgency() {
    return this.userRole === 'agency' || this.userRole === 'agency_member';
  }

  isAgencyOwner() {
    return this.userRole === 'agency' && this.currentAgency?.ownerId === this.currentUser?.uid;
  }

  isAgencyMember() {
    return this.userRole === 'agency_member';
  }

  getAgencyRole() {
    if (this.isAgencyOwner()) return 'owner';
    return this.currentUser?.agencyRole || 'viewer';
  }

  isClient() {
    return this.userRole === 'client';
  }

  canAccessAgencyData(agencyId) {
    if (!this.currentUser) return false;
    if (this.isAgency()) return this.currentAgency?.id === agencyId;
    if (this.isClient()) return this.currentUser.agencyId === agencyId;
    return false;
  }

  canAccessClientData(agencyId, clientId) {
    if (!this.currentUser) return false;
    if (this.isAgency()) return this.currentAgency?.id === agencyId;
    if (this.isClient()) {
      return this.currentUser.agencyId === agencyId && this.currentClient?.id === clientId;
    }
    return false;
  }

  canEditClient(agencyId, clientId) {
    if (!this.currentUser) return false;
    if (this.isAgency()) return this.currentAgency?.id === agencyId;
    if (this.isClient()) {
      return this.currentUser.agencyId === agencyId &&
        this.currentClient?.id === clientId &&
        this.currentClient?.accessPermissions?.canEditProfile === true;
    }
    return false;
  }

  canViewReports(agencyId, clientId = null) {
    if (!this.currentUser) return false;
    if (this.isAgency()) return this.currentAgency?.id === agencyId;
    if (this.isClient()) {
      return this.currentUser.agencyId === agencyId &&
        (!clientId || this.currentClient?.id === clientId) &&
        this.currentClient?.accessPermissions?.canViewReports === true;
    }
    return false;
  }

  canManageAgencySettings() {
    if (!this.isAgency()) return false;
    const role = this.getAgencyRole();
    return role === 'owner' || role === 'admin';
  }

  async updateUserProfile(updates) {
    if (!this.currentUser) {
      throw new Error('No user logged in');
    }

    try {
      const updatedUser = await firebaseService.updateUser(this.currentUser.uid, updates);
      this.currentUser = { ...this.currentUser, ...updatedUser };
      this.notifyListeners();
      return this.currentUser;
    } catch (error) {
      throw error;
    }
  }

  async updateAgencyProfile(updates) {
    if (!this.canManageAgencySettings()) {
      throw new Error('Permission denied to update agency settings');
    }

    try {
      const updatedAgency = await firebaseService.updateAgency(this.currentAgency.id, updates);
      this.currentAgency = { ...this.currentAgency, ...updatedAgency };
      this.notifyListeners();
      return this.currentAgency;
    } catch (error) {
      throw error;
    }
  }

  async createClientLogin(clientId, clientEmail) {
    if (!this.isAgency()) {
      throw new Error('Only agencies can create client logins');
    }

    try {
      const clientUserId = `client_${this.currentAgency.id}_${clientId}`;
      const clientUser = {
        uid: clientUserId,
        email: clientEmail,
        displayName: '',
        photoURL: '',
        role: 'client',
        agencyId: this.currentAgency.id,
        clientId: clientId,
        status: 'active'
      };

      await firebaseService.createUser(clientUser);
      await firebaseService.updateClient(this.currentAgency.id, clientId, {
        userId: clientUserId,
        'loginCredentials.isLoginEnabled': true
      });

      return clientUser;
    } catch (error) {
      throw error;
    }
  }

  async disableClientLogin(clientId) {
    if (!this.isAgency()) {
      throw new Error('Only agencies can disable client logins');
    }

    try {
      await firebaseService.updateClient(this.currentAgency.id, clientId, {
        'loginCredentials.isLoginEnabled': false
      });
      return true;
    } catch (error) {
      throw error;
    }
  }

  async updateAgencySubscription(agencyId, subscriptionData) {
    if (!this.isAgency() || this.currentAgency?.id !== agencyId) {
      throw new Error('Permission denied to update agency subscription');
    }

    try {
      const updates = {
        subscription: {
          ...this.currentAgency.subscription,
          ...subscriptionData,
          updatedAt: new Date().toISOString()
        }
      };
      
      const updatedAgency = await this.updateAgencyProfile(updates);
      this.currentAgency.subscription = updatedAgency.subscription;
      this.notifyListeners();
      return updatedAgency;
    } catch (error) {
      throw error;
    }
  }

  getCurrentPlan() {
    return this.currentAgency?.subscription?.plan || 'free';
  }

  getMaxClients() {
    // Primeiro verifica maxClients (campo usado pelo backend)
    if (this.currentUser?.maxClients) {
      return this.currentUser.maxClients;
    }

    // Fallback para clientLimit (campo antigo)
    if (this.currentUser?.clientLimit) {
      return this.currentUser.clientLimit;
    }

    // Caso contrário, calcular baseado no tier da assinatura
    const tier = this.currentUser?.subscriptionTier || 'basic';
    const TIER_LIMITS = {
      basic: 5,
      starter: 5, // Fallback para assinaturas antigas
      professional: 15,
      enterprise: 30
    };

    return TIER_LIMITS[tier] || this.currentAgency?.subscription?.maxClients || 5;
  }

  async getAgencySettings(agencyId) {
    if (!this.isAgency() || this.currentAgency?.id !== agencyId) {
      throw new Error('Permission denied to access agency settings');
    }
    return this.getDecryptedAgencySettings();
  }

  getDecryptedAgencySettings() {
    if (!this.currentAgency?.settings) return null;

    try {
      return encryptionService.decryptObject(
        this.currentAgency.settings, 
        SENSITIVE_FIELDS.API_SETTINGS
      );
    } catch (error) {
      return this.currentAgency.settings;
    }
  }

  async updateAgencySettings(agencyId, settings) {
    if (!this.isAgency() || this.currentAgency?.id !== agencyId) {
      throw new Error('Permission denied to update agency settings');
    }

    try {
      // Extrair campos sensíveis que precisam de criptografia
      const sensitiveFields = SENSITIVE_FIELDS.API_SETTINGS;
      const fieldsToEncrypt = sensitiveFields.filter(field =>
        settings[field] !== undefined &&
        settings[field] !== null &&
        settings[field] !== '' &&
        !String(settings[field]).startsWith('enc_v2_') // Já criptografado
      );

      let encryptedSettings = { ...settings };

      // Criptografar via Cloud Function se houver campos sensíveis
      if (fieldsToEncrypt.length > 0) {
        const dataToEncrypt = {};
        fieldsToEncrypt.forEach(field => {
          dataToEncrypt[field] = settings[field];
        });

        try {
          const result = await encryptionMigrationService.encryptViaCloudFunction(
            dataToEncrypt,
            fieldsToEncrypt
          );

          if (result.success && result.data) {
            // Mesclar campos criptografados
            encryptedSettings = { ...settings, ...result.data };
          }
        } catch (encryptError) {
          // Continuar com dados não criptografados em caso de falha
        }
      }

      const updates = {
        settings: {
          ...this.currentAgency.settings,
          ...encryptedSettings,
          updatedAt: new Date().toISOString()
        }
      };

      const updatedAgency = await this.updateAgencyProfile(updates);

      // Return decrypted settings for immediate use
      return encryptionService.decryptObject(
        updatedAgency.settings,
        SENSITIVE_FIELDS.API_SETTINGS
      );
    } catch (error) {
      throw error;
    }
  }

  getCurrentAgency() {
    return this.currentAgency;
  }

  getCurrentClient() {
    return this.currentClient;
  }

  getUserRole() {
    return this.userRole;
  }

  onUserDataChanged(callback) {
    this.listeners.push(callback);
    // Call immediately with current state
    callback({
      user: this.currentUser,
      agency: this.currentAgency,
      client: this.currentClient,
      role: this.userRole
    });
  }

  notifyListeners() {
    const data = {
      user: this.currentUser,
      agency: this.currentAgency,
      client: this.currentClient,
      role: this.userRole
    };
    
    this.listeners.forEach(callback => callback(data));
  }

  clearUserData() {
    this.currentUser = null;
    this.currentAgency = null;
    this.currentClient = null;
    this.userRole = null;
    this.notifyListeners();
  }

  getUserDisplayName() {
    if (this.currentUser?.displayName) {
      return this.currentUser.displayName;
    }
    
    if (this.currentClient?.name) {
      return this.currentClient.name;
    }
    
    return this.currentUser?.email?.split('@')[0] || 'Usuário';
  }

  getAgencyDisplayName() {
    return this.currentAgency?.name || 'Agência';
  }

  hasFeature(featureName) {
    return this.currentAgency?.subscription?.features?.includes(featureName) || false;
  }

  /**
   * Busca o histórico de pagamentos do usuário
   * @param {number} limit - Número máximo de pagamentos a retornar (default: 50)
   * @returns {Promise<Array>} Lista de pagamentos ordenados por data
   */
  async getPaymentHistory(limit = 50) {
    if (!this.currentUser?.id) {
      return [];
    }

    try {
      const { orderBy, limit: firestoreLimit } = await import('firebase/firestore');

      const payments = await firebaseService.getCollection(
        `users/${this.currentUser.id}/payments`,
        [orderBy('createdAt', 'desc'), firestoreLimit(limit)]
      );

      // Formatar os pagamentos para exibição
      return payments.map(payment => ({
        id: payment.id,
        type: payment.type || 'subscription',
        amount: payment.amount || 0,
        currency: payment.currency || 'BRL',
        status: payment.status || 'completed',
        paypalTransactionId: payment.paypalTransactionId,
        paypalSubscriptionId: payment.paypalSubscriptionId,
        createdAt: payment.createdAt?.toDate?.() || new Date(payment.createdAt) || new Date(),
        description: payment.description || this.getPaymentDescription(payment)
      }));
    } catch (error) {
      return [];
    }
  }

  /**
   * Gera descrição do pagamento baseado no tipo
   */
  getPaymentDescription(payment) {
    const tier = this.currentUser?.subscriptionTier || 'professional';
    const tierNames = {
      basic: 'Basic',
      starter: 'Basic',
      professional: 'Professional',
      enterprise: 'Enterprise'
    };
    const tierName = tierNames[tier] || 'Premium';

    if (payment.type === 'initial') {
      return `Assinatura ${tierName} - Ativação`;
    } else if (payment.type === 'recurring') {
      return `Assinatura ${tierName} - Renovação`;
    } else if (payment.type === 'refund') {
      return `Reembolso - ${tierName}`;
    }
    return `Pagamento - ${tierName}`;
  }
}

export const userService = new UserService();